import type {User} from "../entities/user.entity";
import {formatPersonName} from "./format-person-name.helper";

/**
 * The name to show for whoever holds an edit lock.
 *
 * Three branches, in this order:
 *
 *   1. the row resolves and carries a first OR last name -> `User.toString()`,
 *      i.e. `"Lastname, Firstname (username)"` — the audit-log viewer's proven
 *      rendering for the normal case;
 *   2. the row resolves but BOTH names are empty -> the bare username;
 *   3. the row cannot be resolved at all -> `"Unbekannt"`.
 *
 * BRANCH 2 IS WHY THIS HELPER EXISTS. `User.toString()` keeps the brackets for
 * a holder with no employee record — legitimately nameless — and renders
 * `"(jdoe)"`. The operator's decision is the bare username for that case, and a
 * dialog calling `toString()` directly would show the brackets.
 *
 * The bare-username fallback stays out of `User.toString()`: the audit-log viewer
 * shares it, and moving the fallback into the entity would change how every
 * audit row names its user. The fallback belongs in the caller. (A missing name
 * part no longer leaves its comma there either — ticket AaDUz8N0cLqoz6BVG58uCQ
 * changed audit rows on purpose to "Stefan (jdoe)".)
 *
 * "Nameless" is decided by `formatPersonName`, so names of blanks count as
 * empty here exactly as they do in `User.toString()`.
 *
 * `undefined` is accepted alongside `null` on purpose: the audit-log entity
 * declares `Promise<User> | null` but its viewer still guards with `!!user`, so
 * the codebase's own precedent does not trust that declared type — and this
 * build has no type-check gate to enforce it. Accepting only `null` would let
 * `undefined` reach a property access and throw on the very case branch 3
 * exists to render.
 */
export function formatHolderName(user: User | null | undefined): string {
    if (!user) {
        return 'Unbekannt';
    }

    if (formatPersonName(user.lastName, user.firstName) === '') {
        return user.username;
    }

    return user.toString();
}
